import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Zap, AlertCircle, Check, CreditCard, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import axios from "axios";
import { toast } from "sonner";
import { AgentPaymentButton, isAgentLoggedIn } from "@/components/AgentPaymentFlow";
import { PaymentDisabledBanner } from "@/components/PaymentGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TokenListrik() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const meterId = searchParams.get("id");
  
  const [customerData, setCustomerData] = useState(null);
  const [selectedNominal, setSelectedNominal] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [adminFee, setAdminFee] = useState(1000);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  useEffect(() => {
    if (!meterId) {
      navigate("/");
      return;
    }
    fetchCustomerInfo();
    fetchAdminFee();
    fetchProducts();
  }, [meterId]);

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await axios.get(`${API}/digiflazz/products/token-listrik`);
      const productList = response.data.products || [];
      // Sort by price
      productList.sort((a, b) => a.price - b.price);
      setProducts(productList);
    } catch (err) {
      console.error("Failed to load products:", err);
      toast.error("Gagal memuat daftar produk dari DigiFlazz");
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchAdminFee = async () => {
    try {
      const response = await axios.get(`${API}/admin/fees/token_listrik`);
      setAdminFee(response.data.fee || 1000);
    } catch (err) {
      setAdminFee(1000);
    }
  };

  const fetchCustomerInfo = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Try PLN prepaid inquiry to get customer name using new endpoint
      try {
        const response = await axios.post(`${API}/digiflazz/pln-inquiry`, {
          customer_no: meterId
        });
        
        if (response.data && response.data.customer_name) {
          setCustomerData({
            customer_no: response.data.customer_no || meterId,
            customer_name: response.data.customer_name,
            segment_power: response.data.segment_power || "",
            meter_no: response.data.meter_no || meterId
          });
        } else {
          // Fallback: use meter ID without name verification
          setCustomerData({ 
            customer_no: meterId, 
            customer_name: "Pelanggan PLN Prabayar",
            segment_power: "",
            meter_no: meterId
          });
        }
      } catch (inquiryErr) {
        // Inquiry failed - still allow transaction but warn user
        console.log("PLN inquiry failed, using default:", inquiryErr);
        const errMsg = inquiryErr.response?.data?.detail || "";
        
        // If it's a validation error (not found, invalid), show error
        if (errMsg.includes("tidak ditemukan") || errMsg.includes("tidak valid")) {
          setError(errMsg);
          return;
        }
        
        // Otherwise proceed with default (API might be down)
        setCustomerData({ 
          customer_no: meterId, 
          customer_name: "Pelanggan PLN Prabayar",
          segment_power: "",
          meter_no: meterId
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Gagal mengambil data pelanggan";
      setError(errorMsg);
      setCustomerData({ customer_no: meterId, customer_name: "Pelanggan PLN" });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedNominal) {
      toast.error("Pilih nominal token terlebih dahulu");
      return;
    }
    
    setIsProcessing(true);
    try {
      navigate(`/payment-token?meter_id=${meterId}&nominal=${selectedNominal.price}&sku=${selectedNominal.sku}&fee=${adminFee}&name=${encodeURIComponent(selectedNominal.name)}`);
    } catch (err) {
      toast.error("Gagal memproses pembelian");
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const totalBayar = selectedNominal ? selectedNominal.selling_price + adminFee : 0;

  return (
    <div className="min-h-screen hero-gradient">
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-heading font-semibold text-slate-800">Produk Digital</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Kembali
            </Button>
          </Link>
        </nav>
      </header>

      <main className="container mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-3xl mx-auto">
            <PaymentDisabledBanner />
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-bold text-slate-900">
                    Token Listrik
                  </h1>
                  <p className="text-slate-600 text-sm">
                    No. Meter: <span className="font-semibold text-amber-600">{meterId}</span>
                  </p>
                </div>
              </div>
            </div>

            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-24 w-full rounded-xl" />
                <Skeleton className="h-48 w-full rounded-xl" />
              </div>
            ) : error ? (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : (
              <>
                {/* Customer Info */}
                {customerData && (
                  <Card className="glass-card-solid mb-6 border-2 border-amber-200">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                          <Zap className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-slate-500">Nama Pelanggan</p>
                          {customerData.customer_name && customerData.customer_name !== "Pelanggan PLN Prabayar" ? (
                            <p className="font-heading font-bold text-xl text-slate-800">
                              {customerData.customer_name}
                            </p>
                          ) : (
                            <p className="font-heading font-semibold text-base text-slate-600 italic">
                              Akan tampil di struk setelah transaksi
                            </p>
                          )}
                          {customerData.segment_power && (
                            <p className="text-sm text-amber-600 font-medium">{customerData.segment_power}</p>
                          )}
                        </div>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-amber-700">No. Meter / ID Pelanggan</span>
                          <span className="font-mono font-bold text-amber-800 text-lg">{meterId}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Nominal Selection */}
                <div className="mb-6">
                  <h3 className="font-heading font-semibold text-slate-800 mb-4">
                    Pilih Nominal Token
                    {productsLoading && <Loader2 className="inline w-4 h-4 ml-2 animate-spin" />}
                  </h3>
                  
                  {productsLoading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {[1,2,3].map(i => (
                        <Skeleton key={i} className="h-20 rounded-xl" />
                      ))}
                    </div>
                  ) : products.length === 0 ? (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Tidak ada produk tersedia saat ini. Coba lagi nanti.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {products.map((product) => (
                        <motion.button
                          key={product.sku}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setSelectedNominal(product)}
                          className={`relative p-4 rounded-xl border-2 transition-all text-center ${
                            selectedNominal?.sku === product.sku
                              ? "border-amber-500 bg-amber-50 shadow-md"
                              : "border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm"
                          }`}
                          data-testid={`nominal-${product.sku}`}
                        >
                          {selectedNominal?.sku === product.sku && (
                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                          <Zap className={`w-6 h-6 mx-auto mb-2 ${
                            selectedNominal?.sku === product.sku ? "text-amber-600" : "text-slate-400"
                          }`} />
                          <p className={`font-semibold text-sm ${
                            selectedNominal?.sku === product.sku ? "text-amber-600" : "text-slate-800"
                          }`}>
                            {product.name}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {formatCurrency(product.selling_price)}
                          </p>
                        </motion.button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Purchase Summary */}
                {selectedNominal && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="glass-card border-2 border-amber-200">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold text-slate-800">
                              Ringkasan Pembelian
                            </h3>
                          </div>
                        </div>

                        <div className="bg-slate-50 rounded-xl p-4 mb-4">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-slate-600">{selectedNominal.name}</span>
                            <span className="font-medium">{formatCurrency(selectedNominal.selling_price)}</span>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-slate-600">Biaya Admin</span>
                            <span className="font-medium text-sky-600">{formatCurrency(adminFee)}</span>
                          </div>
                          <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                            <span className="font-semibold text-slate-800">Total Bayar</span>
                            <span className="text-xl font-heading font-bold text-amber-600">
                              {formatCurrency(totalBayar)}
                            </span>
                          </div>
                        </div>

                        {isAgentLoggedIn() ? (
                          <AgentPaymentButton
                            serviceType="token_listrik"
                            customerNo={meterId}
                            customerName={customerData?.customer_name || "Pelanggan PLN Prabayar"}
                            productName={selectedNominal.name}
                            amount={selectedNominal.selling_price}
                            adminFee={adminFee}
                            buyerSkuCode={selectedNominal.sku}
                            onSuccess={() => { 
                              toast.success("Pembelian token diproses!");
                            }}
                          />
                        ) : (
                          <Button
                            onClick={handlePurchase}
                            disabled={isProcessing}
                            className="w-full h-14 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all bg-gradient-to-r from-amber-500 to-orange-600"
                            data-testid="buy-token-btn"
                          >
                            {isProcessing ? (
                              <div className="spinner w-6 h-6" />
                            ) : (
                              "Beli Token"
                            )}
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
